import React, { useEffect, useRef, useState } from 'react';
import Phaser from 'phaser';
import { RiftNavigatorScene } from '../game/scenes/RiftNavigatorScene';
import { Rift } from '../types/rifts';

interface RiftNavigatorWrapperProps {
  playerId: string;
  onClose: () => void;
  playerPosition?: { x: number; y: number };
}

export const RiftNavigatorWrapper: React.FC<RiftNavigatorWrapperProps> = ({ playerId, onClose, playerPosition }) => {
  const gameRef = useRef<Phaser.Game | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedRift, setSelectedRift] = useState<Rift | null>(null);

  useEffect(() => {
    if (!containerRef.current || gameRef.current) return;

    const config: Phaser.Types.Core.GameConfig = {
      type: Phaser.AUTO,
      parent: containerRef.current,
      width: window.innerWidth,
      height: window.innerHeight,
      backgroundColor: '#0a0e1a',
      scene: RiftNavigatorScene,
      physics: {
        default: 'arcade',
        arcade: {
          debug: false,
        },
      },
    };

    gameRef.current = new Phaser.Game(config);

    const initScene = (): void => {
      const scene = gameRef.current?.scene.getScene('RiftNavigatorScene') as RiftNavigatorScene;
      if (!scene) {
        console.error('[RIFT NAVIGATOR] Scene not found');
        return;
      }

      scene.registry.set('playerId', playerId);

      scene.events.on('riftSelected', (rift: Rift) => {
        setSelectedRift(rift);
      });
    };

    const tryInit = (): void => {
      try {
        initScene();
      } catch (error) {
        console.error('[RIFT NAVIGATOR] Failed to initialize scene:', error);
      }
    };

    setTimeout(tryInit, 100);
    setTimeout(tryInit, 500);
    setTimeout(tryInit, 1000);

    return () => {
      if (gameRef.current) {
        gameRef.current.destroy(true);
        gameRef.current = null;
      }
    };
  }, [playerId]);

  const getScene = (): RiftNavigatorScene | null => {
    return gameRef.current?.scene.getScene('RiftNavigatorScene') as RiftNavigatorScene || null;
  };

  const scene = getScene();
  const allRifts = scene?.getRifts() || [];
  const currentRiftId = scene?.getCurrentRiftId() || 'core';
  
  // Default to current rift if no rift is selected yet
  const displayRift = selectedRift || allRifts.find(r => r.id === currentRiftId);
  const connectedScars = displayRift ? scene?.getConnectedScars() || [] : [];

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      background: '#0a0e1a',
    }}>
      {/* Header */}
      <div style={{
        background: 'rgba(10, 14, 26, 0.95)',
        borderBottom: '2px solid #fbbf24',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <h1 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#fbbf24',
            fontFamily: 'Sometype Mono, monospace',
            letterSpacing: '1px',
            margin: 0,
          }}>
            NAVIGATOR'S CHART
          </h1>
          <div style={{
            fontSize: '12px',
            color: '#94a3b8',
            fontFamily: 'Sometype Mono, monospace',
          }}>
            Current Location: <span style={{ color: '#fbbf24' }}>{currentRiftId === 'core' ? 'The Grand Latitude' : (allRifts.find(r => r.id === currentRiftId)?.name || 'Unknown Rift')}</span>
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            padding: '8px 16px',
            background: 'transparent',
            border: '2px solid #fbbf24',
            color: '#fbbf24',
            fontFamily: 'Sometype Mono, monospace',
            fontSize: '12px',
            cursor: 'pointer',
            letterSpacing: '1px',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#fbbf24';
            e.currentTarget.style.color = '#0a0e1a';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = '#fbbf24';
          }}
        >
          CLOSE [ESC]
        </button>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Phaser Canvas */}
        <div ref={containerRef} style={{ flex: 1, position: 'relative' }}>
          {/* Controls Overlay */}
          <div style={{
            position: 'absolute',
            bottom: '20px',
            left: '20px',
            background: 'rgba(10, 14, 26, 0.95)',
            padding: '12px',
            fontFamily: 'Sometype Mono, monospace',
            fontSize: '10px',
            color: '#94a3b8',
            zIndex: 100,
          }}>
            <div style={{ marginBottom: '4px', color: '#fbbf24', fontWeight: 'bold' }}>CONTROLS:</div>
            <div>• SCROLL - Zoom</div>
            <div>• RIGHT-CLICK + DRAG - Pan</div>
            <div>• CLICK NODE - Select Rift</div>
          </div>

          {/* Legend */}
          <div style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            background: 'rgba(10, 14, 26, 0.95)',
            padding: '12px',
            fontFamily: 'Sometype Mono, monospace',
            fontSize: '10px',
            zIndex: 100,
          }}>
            <div style={{ marginBottom: '8px', color: '#fbbf24', fontWeight: 'bold', fontSize: '11px' }}>ANOMALY LEVELS</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '12px', height: '12px', background: '#4488ff' }} />
                <span style={{ color: '#94a3b8' }}>0 - Safe Core</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '12px', height: '12px', background: '#88ff44' }} />
                <span style={{ color: '#94a3b8' }}>1-2 - Low</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '12px', height: '12px', background: '#ffff44' }} />
                <span style={{ color: '#94a3b8' }}>3-4 - Moderate</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '12px', height: '12px', background: '#ffaa44' }} />
                <span style={{ color: '#94a3b8' }}>5-6 - High</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '12px', height: '12px', background: '#ff6644' }} />
                <span style={{ color: '#94a3b8' }}>7-8 - Extreme</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '12px', height: '12px', background: '#ff4444' }} />
                <span style={{ color: '#94a3b8' }}>9+ Deadly</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Rift Details */}
        {displayRift && (
          <div style={{
            width: '360px',
            background: 'rgba(10, 14, 26, 0.95)',
            borderLeft: '2px solid #fbbf24',
            overflowY: 'auto',
            padding: '20px',
            fontFamily: 'Sometype Mono, monospace',
            fontSize: '11px',
            color: '#e2e8f0',
          }}>
            <div style={{ marginBottom: '16px', borderBottom: '2px solid #fbbf24', paddingBottom: '12px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#fbbf24', margin: '0 0 8px 0', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                {displayRift.name}
              </h2>
              {displayRift.theme && (
                <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'capitalize', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                  {displayRift.theme} Theme
                </div>
              )}
            </div>

            <div style={{ marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid #fbbf24' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div>
                  <div style={{ color: '#64748b', fontSize: '9px', marginBottom: '2px' }}>LAYER</div>
                  <div style={{ color: '#fbbf24', fontWeight: 'bold' }}>Layer {displayRift.layer}</div>
                </div>
                <div>
                  <div style={{ color: '#64748b', fontSize: '9px', marginBottom: '2px' }}>SIZE</div>
                  <div style={{ color: '#e2e8f0' }}>{displayRift.width}x{displayRift.height}</div>
                </div>
                <div>
                  <div style={{ color: '#64748b', fontSize: '9px', marginBottom: '2px' }}>ANOMALY</div>
                  <div style={{ color: '#fbbf24', fontWeight: 'bold' }}>{displayRift.anomalyLevel}/10</div>
                </div>
                {displayRift.minLevel && (
                  <div>
                    <div style={{ color: '#64748b', fontSize: '9px', marginBottom: '2px' }}>MIN LEVEL</div>
                    <div style={{ color: '#ff6644', fontWeight: 'bold' }}>{displayRift.minLevel}</div>
                  </div>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid #fbbf24' }}>
              <div style={{ color: '#64748b', fontSize: '9px', marginBottom: '4px' }}>STATUS</div>
              <div style={{ color: displayRift.discoveredAt ? '#10b981' : '#64748b' }}>
                {displayRift.discoveredAt ? 'DISCOVERED' : 'UNDISCOVERED'}
              </div>
              {displayRift.discoveredBy && (
                <div style={{ color: '#94a3b8', fontSize: '10px', marginTop: '4px' }}>
                  Discovered by: {displayRift.discoveredBy}
                </div>
              )}
            </div>

            {connectedScars.length > 0 && (
              <div>
                <div style={{ color: '#fbbf24', fontWeight: 'bold', marginBottom: '8px', fontSize: '12px' }}>
                  RIFT SCARS ({connectedScars.length})
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {connectedScars.map(scar => {
                    const chunkX = Math.floor(scar.worldX / 1000);
                    const chunkY = Math.floor(scar.worldY / 1000);
                    return (
                      <div
                        key={scar.id}
                        style={{
                          background: 'rgba(0, 0, 0, 0.3)',
                          border: '1px solid #475569',
                          padding: '10px',
                          fontSize: '10px',
                        }}
                      >
                        <div style={{ color: '#fbbf24', fontWeight: 'bold', marginBottom: '4px', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                          {scar.name}
                        </div>
                        <div style={{ color: '#94a3b8', marginBottom: '4px', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                          TO: {allRifts.find(r => r.id === scar.destinationRift)?.name || 'Unknown'}
                        </div>
                        <div style={{ color: '#64748b', fontSize: '9px', marginBottom: '4px' }}>
                          Chunk: ({chunkX}, {chunkY})
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: '#64748b' }}>Danger: {scar.dangerLevel}/10</span>
                          <span style={{
                            padding: '2px 6px',
                            fontSize: '9px',
                            background: scar.stability === 'sealed' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(59, 130, 246, 0.2)',
                            color: scar.stability === 'sealed' ? '#ef4444' : '#3b82f6',
                          }}>
                            {scar.stability === 'sealed' ? 'SEALED' : 'OPEN'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Always show current location info if we have player position */}
            {playerPosition && displayRift.id === currentRiftId && (
              <div style={{
                marginTop: '16px',
                background: 'rgba(251, 191, 36, 0.1)',
                border: '1px solid #fbbf24',
                padding: '10px',
                fontSize: '10px',
                color: '#fbbf24',
              }}>
                <strong>YOUR LOCATION</strong>
                <div style={{ marginTop: '4px', color: '#94a3b8' }}>
                  You are currently in this rift.
                </div>
                <div style={{ marginTop: '6px', color: '#64748b', fontSize: '9px' }}>
                  Your Chunk: ({Math.floor(playerPosition.x / 1000)}, {Math.floor(playerPosition.y / 1000)})
                </div>
              </div>
            )}
            
            {/* Show location indicator when viewing a different rift */}
            {playerPosition && displayRift.id !== currentRiftId && currentRiftId !== 'core' && (
              <div style={{
                marginTop: '16px',
                background: 'rgba(100, 116, 139, 0.1)',
                border: '1px solid #64748b',
                padding: '10px',
                fontSize: '10px',
                color: '#94a3b8',
              }}>
                <strong>YOUR CURRENT LOCATION</strong>
                <div style={{ marginTop: '4px', color: '#64748b', fontSize: '9px' }}>
                  You are in: {allRifts.find(r => r.id === currentRiftId)?.name || 'Unknown Rift'}
                </div>
                <div style={{ marginTop: '4px', color: '#64748b', fontSize: '9px' }}>
                  Your Chunk: ({Math.floor(playerPosition.x / 1000)}, {Math.floor(playerPosition.y / 1000)})
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer Stats */}
      <div style={{
        background: 'rgba(10, 14, 26, 0.95)',
        borderTop: '2px solid #fbbf24',
        padding: '12px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '10px',
        fontFamily: 'Sometype Mono, monospace',
        color: '#94a3b8',
      }}>
        <div style={{ display: 'flex', gap: '24px' }}>
          <div>Rifts: <span style={{ color: '#fbbf24', fontWeight: 'bold' }}>{allRifts.filter(r => r.id !== 'core').length}</span></div>
          <div>Discovered: <span style={{ color: '#10b981', fontWeight: 'bold' }}>{allRifts.filter(r => r.discoveredAt && r.id !== 'core').length}</span></div>
          <div>Rift Scars: <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>{getScene()?.getScars().length || 0}</span></div>
        </div>
        <div style={{ color: '#64748b', fontSize: '9px' }}>
          Navigate the uncharted waters between rifts
        </div>
      </div>
    </div>
  );
};
